import React, { useState, useRef, useCallback } from 'react';
import expensesApi from '../api/expenses';
import LoadingOverlay from './LoadingOverlay';

interface AudioExpenseRecorderProps {
  onSaved?: () => void;
}

const AudioExpenseRecorder: React.FC<AudioExpenseRecorderProps> = ({ onSaved }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setMessage('');
          } catch (error) {
      console.error('Error accessing microphone:', error);
      setMessage('Не удалось получить доступ к микрофону');
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  const processAudio = async () => {
    if (!audioBlob) return;

    setIsProcessing(true);
    setMessage('');
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, `recording-${Date.now()}.webm`);

      const parsed = await expensesApi.previewAudio(formData);
      // Save directly without preview
      await expensesApi.create(parsed);
      setAudioBlob(null);
      setAudioUrl(null);
      setMessage(parsed.type === 'income' ? 'Доход успешно добавлен!' : 'Расход успешно добавлен!');
      onSaved?.();
    } catch (error: any) {
      console.error('Error processing audio:', error);
      setMessage(error.response?.data?.message || 'Ошибка при обработке аудио');
    } finally {
      setIsProcessing(false);
    }
  };

  const discardRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setMessage('');
      };

  return (
    <div className="relative">
      <LoadingOverlay isVisible={isProcessing} />
      <p className="text-sm text-gray-500 mb-4">
        Надиктуйте расход или доход, например: "Двенадцатого февраля оплата аренды офиса две тысячи пятьсот евро" или "Пятнадцатого марта получили оплату от клиента пять тысяч рублей"
      </p>

      {!audioBlob ? (
        <div className="text-center">
          <button
            onClick={isRecording ? stopRecording : startRecording}
            className={`px-6 py-3 rounded-full font-medium transition-colors ${
              isRecording
                ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {isRecording ? 'Остановить запись' : 'Начать запись'}
          </button>
          {isRecording && (
            <p className="mt-2 text-gray-600">Запись идёт...</p>
          )}
        </div>
      ) : (
        <div>
          <div className="mb-4">
            <audio src={audioUrl!} controls className="w-full" />
          </div>

          <div className="flex gap-3">
            <button
              onClick={processAudio}
              disabled={isProcessing}
              className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-400 transition-colors"
            >
              {isProcessing ? 'Распознавание...' : 'Распознать'}
            </button>
            <button
              onClick={discardRecording}
              disabled={isProcessing}
              className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 disabled:bg-gray-400 transition-colors"
            >
              Отменить
            </button>
          </div>
        </div>
      )}

      {message && (
        <p className={`mt-4 text-sm text-center ${message.includes('успешно') ? 'text-green-600' : 'text-red-600'}`}>
          {message}
        </p>
      )}
    </div>
  );
};

export default AudioExpenseRecorder;
